/* eslint-disable react/destructuring-assignment */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable react/function-component-definition */
import i18next from 'i18next'
import React from 'react'
import Select, {
  components,
  DropdownIndicatorProps,
  GroupBase,
  Props,
  StylesConfig,
} from 'react-select'
import AsyncSelect, { AsyncProps } from 'react-select/async'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { theme } from '../../../theme/theme'
import { Icon, IconChoices } from '../Icon'

export interface SelectOption {
  label: string
  value: string | number
}

export interface SelectDropdownStyleProps {
  showDropdownIndicator?: boolean
  error?: boolean
  menuListHeight?: number
  menuListWidth?: string | number
  borderless?: boolean
  height?: number | string
  minHeight?: number | string
}

function DropdownIndicator<
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(props: DropdownIndicatorProps<Option, IsMulti, Group>) {
  return (
    <components.DropdownIndicator {...props}>
      <Icon icon={IconChoices.CHEVRON_DOWN} />
    </components.DropdownIndicator>
  )
}

function getSelectStyles<
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>({
  error,
  menuListHeight = 300,
  menuListWidth = '100%',
  borderless = false,
  showDropdownIndicator = true,
  height = 'fit-content',
  minHeight = 48,
  isDisabled,
}: SelectDropdownStyleProps & {
  isDisabled?: boolean
}): StylesConfig<Option, IsMulti, Group> {
  return {
    option: (provided, state) => {
      const getBackgroundColor = (): string => {
        if (state.isSelected) return '#F2F2F2'
        if (state.isFocused) return '#F2F2F2'
        return 'white'
      }
      const backgroundColor = getBackgroundColor()
      return {
        ...provided,
        cursor: 'pointer',
        backgroundColor,
        color: 'black',
        outline: 'unset',
        ':active': {
          backgroundColor: '#F2F2F2',
        },
      }
    },
    singleValue: (provided) => {
      return {
        ...provided,
        ...theme.textVariants.base,
        color: '#010101',
      }
    },
    multiValue: (provided) => {
      return {
        ...provided,
        ...theme.textVariants.baseBold,
      }
    },
    multiValueLabel: (provided) => {
      return {
        ...provided,
        ...theme.textVariants.baseBold,
      }
    },
    multiValueRemove: (provided) => {
      return {
        ...provided,
        paddingLeft: 8,
        paddingRight: 8,
      }
    },
    menu: (provided) => {
      return {
        ...provided,
        width: menuListWidth,
        overflow: 'hidden',
      }
    },
    menuPortal: (provided) => {
      return {
        ...provided,
        zIndex: 20,
      }
    },
    menuList: (provided) => {
      return {
        ...provided,
        maxHeight: menuListHeight,
        padding: 0,
      }
    },
    control: (provided, state) => {
      const getBorder = (): string => {
        if (error) return '1px solid #DD3322'
        if (state.isFocused) return '2px solid rgba(17, 102, 187, 0.5)'
        if (state.menuIsOpen) return '2px solid rgba(17, 102, 187, 0.5)'
        if (state.isDisabled) return '1px solid #CCCCCC'
        return '1px solid #D1D5DB'
      }
      const getBackgroundColor = (): string => {
        if (state.isDisabled) return '#FAFAFA'
        return 'white'
      }
      const getHoverBorder = (): string => {
        if (borderless) return 'none'
        if (state.isFocused) return '2px solid rgba(17, 102, 187, 0.5)'
        return '1px solid #B3B3B3'
      }
      const backgroundColor = getBackgroundColor()
      const border = getBorder()
      const hoverBorder = getHoverBorder()
      return {
        ...provided,
        ...(isDisabled ? { opacity: '50%' } : {}),
        cursor: 'pointer',
        border: borderless ? 'none' : border,
        ':hover': {
          border: hoverBorder,
        },
        boxShadow: 'none',
        minHeight,
        height: height || 'fit-content',
        backgroundColor,
        paddingLeft: 6,
        paddingRight: 8,
      }
    },
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: () => ({
      display: showDropdownIndicator ? 'inline-block' : 'none',
      marginRight: 7,
    }),
  }
}

export function SelectDropdown<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: Props<Option, IsMulti, Group> & SelectDropdownStyleProps) {
  const {
    error,
    placeholder = i18next.t('common.actions.select'),
    menuListHeight,
    menuListWidth,
    borderless,
    showDropdownIndicator,
    height,
    minHeight,
    ...rest
  } = props
  const { translate } = useAppTranslation()
  const customStyles = getSelectStyles<Option, IsMulti, Group>({
    error,
    menuListHeight,
    menuListWidth,
    borderless,
    showDropdownIndicator,
    height,
    minHeight,
    isDisabled: props.isDisabled,
  })

  const getNoOptionsMessage = (): string => {
    return translate('select.noOptions')
  }

  return (
    <Select
      placeholder={placeholder}
      noOptionsMessage={getNoOptionsMessage}
      components={{ DropdownIndicator }}
      styles={customStyles}
      menuPosition="fixed"
      {...rest}
    />
  )
}

export function AsyncSelectDropdown<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: AsyncProps<Option, IsMulti, Group> & SelectDropdownStyleProps) {
  const {
    error,
    placeholder = i18next.t('common.actions.select'),
    menuListHeight,
    menuListWidth,
    borderless,
    showDropdownIndicator,
    height,
    minHeight,
    ...rest
  } = props
  const { translate } = useAppTranslation()
  const customStyles = getSelectStyles<Option, IsMulti, Group>({
    error,
    menuListHeight,
    menuListWidth,
    borderless,
    showDropdownIndicator,
    height,
    minHeight,
    isDisabled: props.isDisabled,
  })

  const getNoOptionsMessage = (): string => {
    return translate('select.noOptions')
  }

  return (
    <AsyncSelect
      placeholder={placeholder}
      noOptionsMessage={getNoOptionsMessage}
      components={{ DropdownIndicator }}
      styles={customStyles}
      menuPosition="fixed"
      {...rest}
    />
  )
}
